import { ServiceFlowConfigurator } from '../service-flow-configurator';
import {
  readSessionStorageJson,
  writeSessionStorageFlag,
  SERVICE_FLOW_STORAGE_KEYS,
} from '../storage';
import { buildItemCard } from './item-card';
import { ItemRemoval } from './item-removal';
import { handleSubmitSuccess } from './submit-dialog';
import { showToast, ToastType } from '../../toast';
import { buildSubmitConfigs, submitOrder, matchSubmitErrors } from './helpers/submit';
import { PurchaseOrder } from './helpers/purchase-order';

export class ServiceFlowWarranty {
  constructor(element) {
    this._refs = {
      el: element,
      userId: element.dataset.userId,
      apiKey: element.dataset.apiKey,
      warrantyApiUrl: element.dataset.warrantyApiUrl,
      configuratorName: element.dataset.configuratorName,
      configuratorContainer: element.querySelector('[data-service-flow-configurator-container]'),
      purchaseOrderContainer: element.querySelector('[data-service-flow-purchase-order]'),
      submitButton: document.querySelector('.next-step-bar__button--primary'),
    };

    this._warrantyConfig = this._parseConfig(element.dataset.serviceFlowWarrantyConfig);
    this.translations = this._warrantyConfig?.Translations || {};

    this._purchaseOrder = new PurchaseOrder({
      container: element.querySelector('[data-service-flow-purchase-order]'),
      config: this._warrantyConfig?.PurchaseOrder,
      action: this._warrantyConfig?.ConfiguratorAction,
      getItems: () => this._getItems(),
    });

    this._itemRemoval = new ItemRemoval({
      container: this._refs.configuratorContainer,
      translations: this.translations,
      onItemsChanged: () => {
        const itemCount = this._getItems().length;
        this._updateSameReasonsVisibility(itemCount);
        this._purchaseOrder.updateVisibility();
      },
    });

    this.init();
    this.addEventListeners();
  }

  init() {
    const configuratorName = this._refs.configuratorName;
    const items = this._getItems();
    this._itemRemoval.setItemOrder(items);
    this._updateSameReasonsVisibility(items.length);

    if (!items.length) {
      this._redirectToFirstStep();
      return;
    }

    this._removeLoadingSkeletons();
    this._purchaseOrder.render();
    this._purchaseOrder.updateVisibility();

    const canLoadConfigurators = this._refs.configuratorContainer && configuratorName;
    const cards = canLoadConfigurators
      ? items.map((item) => this._buildAndAppendItemCard(item))
      : [];

    if (canLoadConfigurators) {
      this._loadItemConfigurators(cards, configuratorName);
    } else {
      console.error(
        'ServiceFlowWarranty: cannot load configurators - missing configuratorContainer or configuratorName',
        { hasConfiguratorContainer: !!this._refs.configuratorContainer, configuratorName },
      );
    }
  }

  _redirectToFirstStep() {
    const firstStepUrl = this._warrantyConfig?.FirstStepUrl;

    if (firstStepUrl) {
      writeSessionStorageFlag(SERVICE_FLOW_STORAGE_KEYS.NO_DATA_WARNING, true);
      window.location.replace(firstStepUrl);
    }
  }

  _getItems() {
    const items = readSessionStorageJson(SERVICE_FLOW_STORAGE_KEYS.ITEMS, []);

    if (Array.isArray(items) && items.length) {
      return items;
    }

    return [];
  }

  async _loadItemConfigurators(cards, configuratorName) {
    try {
      const entries = cards.map((card) => this._createConfiguratorForCard(card));

      // Fetch every card's configurator data in parallel.
      const apiDataList = await Promise.all(
        entries.map(({ configurator, item }) =>
          configurator.fetchConfiguratorData(item.serialNo, configuratorName),
        ),
      );

      await Promise.all(
        entries.map(({ configurator }, i) => configurator.renderConfigurator(apiDataList[i])),
      );
    } catch (error) {
      console.error('ServiceFlowWarranty: failed to render configurators', error);
    }
  }

  _removeLoadingSkeletons() {
    this._refs.configuratorContainer
      ?.querySelectorAll('[data-loading-skeleton]')
      .forEach((skeleton) => skeleton.remove());

    document
      .querySelector('[data-item-cards-same-reasons]')
      ?.classList.remove('item-card__same-reasons--loading');
  }

  _updateSameReasonsVisibility(itemCount) {
    const sameReasons = document.querySelector('[data-item-cards-same-reasons]');
    if (!sameReasons) return;

    sameReasons.toggleAttribute('hidden', itemCount < 2);
  }

  _buildAndAppendItemCard(item) {
    const card = buildItemCard(item, this.translations, this._warrantyConfig, (c, i) =>
      this._itemRemoval.removeCard(c, i),
    );
    this._refs.configuratorContainer.appendChild(card);
    return { card, item };
  }

  _createConfiguratorForCard({ card, item }) {
    // Only the configurator/form fetch has a loading state; card data renders synchronously.
    const mount = card.querySelector('.item-card__configurator-mount');
    const configurator = new ServiceFlowConfigurator(
      mount,
      this.onConfiguratorValidationChange.bind(this),
      this.translations,
      {
        apiKey: this._warrantyConfig?.ApiKey,
        userId: this._warrantyConfig?.UserId,
        warrantyApiUrl: this._warrantyConfig?.WarrantyApiUrl,
        dateFormat: this._warrantyConfig?.DateFormat,
        payloadDateFormat: this._warrantyConfig?.PayloadDateFormat,
      },
      item.productName,
    );

    // Lets card lookups resolve the instance from the DOM (also after the mount is moved).
    mount.serviceFlowConfigurator = configurator;

    return { configurator, item };
  }

  addEventListeners() {
    this._bindSubmitButton();

    document.addEventListener('item-cards-same-reasons:toggle', () => this._resetValidationState());
  }

  // Active cards are the ones in the DOM that carry data-item-card (removed / merged-out cards don't).
  _getActiveConfigurators() {
    return this._getConfiguratorsBySelector('[data-item-card] .item-card__configurator-mount');
  }

  // Configurators of individual cards hidden by "same for all"; they take the merged card's values.
  _getMergedOutConfigurators() {
    return this._getConfiguratorsBySelector(
      '[data-item-card-inactive] .item-card__configurator-mount',
    );
  }

  _getConfiguratorsBySelector(selector) {
    return [...document.querySelectorAll(selector)]
      .map((mount) => mount.serviceFlowConfigurator)
      .filter(Boolean);
  }

  // Clears errors/isSubmitted on every configurator still in the DOM and removes the current toast.
  _resetValidationState() {
    this._getConfiguratorsBySelector('.item-card__configurator-mount').forEach((configurator) =>
      configurator.resetValidation(),
    );

    if (this._mandatoryStepsToast && !this._mandatoryStepsToast.isDismissed) {
      this._mandatoryStepsToast.dismiss();
    }
    this._mandatoryStepsToast = null;
  }

  // Binds the real submit button
  _bindSubmitButton() {
    if (!this._refs.submitButton) {
      return;
    }

    this._refs.submitButton.addEventListener('click', (event) => {
      event.preventDefault();
      this._onSubmitButtonClick();
    });
  }

  // Validates every card independently; proceeds only once all cards are valid.
  _onSubmitButtonClick() {
    // Nothing to submit when no configurator was loaded.
    if (this._isSubmitting || !this._getActiveConfigurators().length) return;

    const purchaseOrderValidation = this._purchaseOrder.validate();

    const results = this._validateAllCards();
    const hasInvalidCard = Object.values(results).some((result) => !result.isValid);

    if (!purchaseOrderValidation.isValid || hasInvalidCard) {
      this._showMandatoryStepsToast();
      return;
    }

    this._hideMandatoryStepsToast();

    // All cards valid - send the configurators to the backend.
    this._submitConfigurators();
  }

  async _submitConfigurators() {
    if (this._isSubmitting) return;
    this._isSubmitting = true;
    this._setSubmitLoading(true);

    // On redirect the page navigates away, so the loading state is kept until then.
    let keepLoading = false;

    try {
      const action = this._warrantyConfig?.ConfiguratorAction;
      const configs = await buildSubmitConfigs({
        activeConfigurators: this._getActiveConfigurators(),
        mergedOutConfigurators: this._getMergedOutConfigurators(),
        action,
        purchaseOrderNumber: this._purchaseOrder.getValue(),
      });

      const results = await submitOrder(configs);
      const failed = (results || []).filter((r) => !r?.Result?.Success);

      if (failed.length) {
        this._showSubmitErrors(failed);
        return;
      }

      keepLoading = this._onSubmitSuccess();
    } catch (error) {
      console.error('Submit order failed:', error);
      this._showSubmitErrors([], error?.serverMessage);
    } finally {
      this._isSubmitting = false;
      if (!keepLoading) {
        this._setSubmitLoading(false);
      }
    }
  }

  _setSubmitLoading(isLoading) {
    const button = this._refs.submitButton;
    if (!button) return;

    button.classList.toggle('next-step-bar__button--loading', isLoading);
    button.disabled = isLoading;
    button.setAttribute('aria-busy', String(isLoading));
  }

  _showSubmitErrors(failedResults, serverMessage) {
    const { unmatchedErrors, hasInlineErrors } = matchSubmitErrors({
      failedResults,
      configurators: this._getActiveConfigurators(),
    });

    if (hasInlineErrors && !unmatchedErrors.length) {
      this._showMandatoryStepsToast();
      return;
    }

    const messages = unmatchedErrors
      .map((e) => {
        const fieldName = e?.FieldDisplayName || e?.FieldName;
        return [e?.ErrorType, fieldName].filter(Boolean).join(': ');
      })
      .filter(Boolean);

    showToast({
      iconKey: 'alert-circle',
      message: messages.length ? messages.join('. ') : serverMessage || 'Something went wrong',
      type: ToastType.ERROR,
      autoDismiss: 50000,
    });
  }

  _onSubmitSuccess() {
    return handleSubmitSuccess(this._warrantyConfig, this._getItems());
  }

  // Runs validate() on every active card, keyed by its position.
  _validateAllCards() {
    const results = {};

    this._getActiveConfigurators().forEach((configurator, index) => {
      results[index] = { isValid: configurator.validate() };
    });

    return results;
  }

  // Toast mount point positioned above the next-step-bar. Created lazily/once.
  _getMandatoryStepsToastContainer() {
    if (this._mandatoryStepsToastContainer) return this._mandatoryStepsToastContainer;

    const nextStepBar = this._refs.submitButton?.closest('[data-next-step-bar]');
    if (!nextStepBar) return null;

    const container = document.createElement('div');
    // Base .toast-container class is added by Toast.show(); only the positioning modifier is needed here.
    container.className = 'next-step-bar__toast-container';
    nextStepBar.insertBefore(container, nextStepBar.firstChild);

    this._mandatoryStepsToastContainer = container;
    return container;
  }

  _showMandatoryStepsToast(message) {
    const toastMessage =
      message ||
      this.translations?.requiredSelectionsAreMissing ||
      'You have mandatory steps to complete';

    // Reuse the existing toast instead of showing a stale one on repeat submits.
    if (this._mandatoryStepsToast && !this._mandatoryStepsToast.isDismissed) {
      this._mandatoryStepsToast.updateMessage(message);
      return;
    }

    this._mandatoryStepsToast = showToast({
      iconKey: 'alert-circle',
      message: toastMessage,
      type: ToastType.ERROR,
      autoDismiss: 0,
      container: this._getMandatoryStepsToastContainer(),
    });
  }

  _hideMandatoryStepsToast() {
    this._mandatoryStepsToast?.dismiss();
    this._mandatoryStepsToast = null;
  }

  _parseConfig(rawConfig) {
    if (!rawConfig) {
      return null;
    }

    try {
      return JSON.parse(rawConfig);
    } catch (error) {
      console.error('Failed to parse service-flow-warranty-config:', error);
      return null;
    }
  }

  // Submit button stays enabled; validation runs on click and errors show per-field.
  onConfiguratorValidationChange() {
    return this._getActiveConfigurators().every((c) => c.isAllFieldValidationsValid);
  }
}
